// Derivaciones de Análisis — lógica PURA, sin React.
//
// Todo lo de aquí existe porque el silo manda lo mínimo y la pantalla necesita más:
//   · el mix llega por fila con solo las celdas que vendieron → se pivota y se completan los ceros
//   · la rotación llega SIN nombre de tienda (otro lambda) → se cruza por `storeId`
//   · el ranking de vendedores llega completo → los extremos top/bottom se cortan aquí
import type {
    SellerRankRow,
    StoreCohortRow,
    StoreFinancierMixRow,
} from '@/api/services/retail-wizard/backoffice';
import type { StoreRotationRow } from '@/api/services/retail-catalog/backoffice';
import type { Financier } from '@/api/services/retail-org/backoffice';

/** Fila pivotada de la matriz: la tienda con sus piezas por financiera y su total. */
export interface PivotedStoreRow {
    storeId: string;
    storeName: string;
    byFinancier: Record<Financier, number>;
    total: number;
}

/**
 * Pivota el mix a filas densas sobre las financieras dadas.
 *
 * El silo manda solo las celdas con venta; los ceros se completan aquí. Mandar la matriz densa por la
 * red significaría transmitir los ceros, que son la mayoría.
 */
export function pivotMatrix(
    rows: StoreFinancierMixRow[],
    financiers: Financier[],
): PivotedStoreRow[] {
    return rows.map((row) => {
        const byFinancier = Object.fromEntries(financiers.map((f) => [f, 0])) as Record<
            Financier,
            number
        >;
        for (const cell of row.cells) {
            byFinancier[cell.financier] = cell.units;
        }
        return {
            storeId: row.storeId,
            // Nunca el ULID crudo: 26 caracteres que rompen la columna y no informan.
            storeName: row.storeName ?? shortStoreLabel(row.storeId),
            byFinancier,
            total: row.totalUnits,
        };
    });
}

/**
 * Las financieras que de verdad aparecen en el periodo, en orden fijo.
 *
 * No se pintan las seis siempre: una columna en cero para todas las tiendas ocupa ancho y no
 * informa. Pero el ORDEN es fijo —no el de aparición— o las columnas se reacomodan al cambiar de
 * pill y la matriz deja de ser comparable de un vistazo.
 */
export function financiersPresent(rows: StoreFinancierMixRow[], order: Financier[]): Financier[] {
    const present = new Set(rows.flatMap((row) => row.cells.map((cell) => cell.financier)));
    return order.filter((financier) => present.has(financier));
}

/** Participación de una celda sobre el total de su tienda. 0 si la tienda no vendió nada. */
export function cellShare(row: PivotedStoreRow, financier: Financier): number {
    return row.total > 0 ? row.byFinancier[financier] / row.total : 0;
}

/** Cohorte ordenada de mejor a peor variación; las sin base van al final. */
export function sortCohort(rows: StoreCohortRow[]): StoreCohortRow[] {
    return [...rows].sort((a, b) => {
        if (a.centsChangePct === null) return 1;
        if (b.centsChangePct === null) return -1;
        return b.centsChangePct - a.centsChangePct;
    });
}

/**
 * Los extremos del ranking: los `size` mejores y los `size` peores.
 *
 * Con pocos vendedores los dos bloques se traslaparían y el mismo nombre saldría en «top» y en
 * «bottom» — que es exactamente la lectura que no queremos. Debajo de `size * 2` vendedores, el
 * bottom se recorta para que no haya solapamiento.
 */
export function sellerExtremes(
    rows: SellerRankRow[],
    size = 5,
): { top: SellerRankRow[]; bottom: SellerRankRow[] } {
    const top = rows.slice(0, size);
    const bottom = rows.slice(Math.max(size, rows.length - size)).reverse();
    return { top, bottom };
}

/**
 * Por qué el silo no mandó porcentaje. `changePct: null` tiene tres causas y la pantalla las pintaba
 * todas con el mismo guion.
 */
export type ChangeState =
    | { kind: 'pct'; pct: number }
    | { kind: 'fromZero' }
    | { kind: 'noActivity' }
    | { kind: 'noBaseline' };

/**
 * Clasifica la variación usando la base que el silo SÍ manda (`previousUnits` / `previousCents`).
 * Con base en cero y ventas hoy hay crecimiento real, aunque no haya porcentaje que calcular.
 */
export function changeState(
    changePct: number | null,
    current: number,
    previous: number | null,
): ChangeState {
    if (changePct !== null) return { kind: 'pct', pct: changePct };
    if (previous !== 0) return { kind: 'noBaseline' };
    return current > 0 ? { kind: 'fromZero' } : { kind: 'noActivity' };
}

/** Fila de rotación ya cruzada con el nombre de su tienda. */
export interface NamedRotationRow extends StoreRotationRow {
    storeName: string;
}

/**
 * Cruza la rotación (que no trae nombre) con los nombres que sí vienen en el análisis.
 *
 * La tienda que rota pero no vendió en el periodo no está en el mix, y aun así tiene que salir: cae
 * a su `storeId`, que es feo pero verdadero. Ordena la MÁS LENTA primero — es la que hay que mirar —
 * y las que no tienen ventas van al final, porque su `null` no es «rota rapidísimo».
 */
export function namedRotation(
    rows: StoreRotationRow[],
    namesByStoreId: Map<string, string>,
): NamedRotationRow[] {
    return rows
        .filter(hasSomethingToReport)
        .map((row) => ({
            ...row,
            storeName: namesByStoreId.get(row.storeId) ?? shortStoreLabel(row.storeId),
        }))
        .sort((a, b) => {
            if (a.averageRotationDays === null) return 1;
            if (b.averageRotationDays === null) return -1;
            return b.averageRotationDays - a.averageRotationDays;
        });
}

/**
 * ¿Esta fila tiene algo que reportar, en la dimensión que sea?
 *
 * La regla es angosta a propósito: **fuera solo si no tiene NI stock NI ventas**. Una tienda con una
 * sola pieza en piso se queda, aunque no tenga nombre ni haya vendido nunca — esa pieza existe
 * físicamente y es justo lo que el tablero de inventario busca señalar. Esconderla sería peor que el
 * ruido que quita.
 *
 * Lo que se va es la fila con `0` en stock, `0` SKUs, `0` vendidas y rotación `null`: no aporta un
 * solo bit en ninguna columna. Hoy en Dev es `01KXFAKESTORE…`, pero la regla no es un parche para el
 * seed: en producción pasa igual cada vez que se da de alta una tienda y nunca se surte.
 *
 * ⚠️ **El filtro vive en el FRONT, no en la API.** El endpoint es compartido y otro consumidor puede
 * querer el set completo; qué filas vale la pena pintar es una decisión de presentación.
 *
 * 🔴 El riesgo asumido: esconder filas en silencio puede tapar un problema real. Por eso la
 * condición incluye `soldUnits` — una tienda que se quedó sin stock **pero vendió alguna vez** tiene
 * `soldUnits > 0` y sigue visible, que es exactamente el caso que no se quiere perder de vista.
 */
function hasSomethingToReport(row: StoreRotationRow): boolean {
    return row.totalStock > 0 || row.soldUnits > 0;
}

/** Cuántas filas esconde `namedRotation`, para que la tabla pueda decir que no las pinta. */
export function hiddenRotationCount(rows: StoreRotationRow[]): number {
    return rows.filter((row) => !hasSomethingToReport(row)).length;
}

/**
 * Etiqueta de una tienda cuyo nombre no conocemos.
 *
 * Pasa de verdad: el inventario devuelve toda tienda con piezas, y una que nunca vendió no aparece
 * en el mix —que es de donde salen los nombres—. Volcar el ULID de 26 caracteres rompe la columna y
 * no le dice nada a nadie.
 *
 * ⚠️ Los últimos 6 caracteres de un ULID **no identifican nada**: son la cola del contador, no un
 * nombre. Por eso la etiqueta dice que el nombre FALTA en vez de fingir uno con «Tienda …X69XC7».
 * Cuatro filas de la tabla salen así hoy, y quien las lee necesita entender que el hueco está en el
 * padrón, no en la tienda. El sufijo se conserva porque es lo único con qué reportarla.
 *
 * Y tiene que seguir siendo CORTA. El primer intento («Tienda sin nombre (…X69XC7)») medía 30
 * caracteres: más que el ULID crudo que quería evitar, así que rompía la columna igual. El test de
 * longitud lo cazó. Esta dice lo mismo en 18.
 *
 * 🔴 Se va cuando el silo consuma `GET /private/retailers/{retailerId}/stores` de
 * `retail-org-business` —ya desplegado en Dev—, que es el padrón de verdad. Bloqueado por el publish
 * de `@fiado/api-invoker` 5.22.0.
 *
 * No se oculta la fila: esa tienda TIENE stock parado, que es justo lo que el tablero busca mostrar.
 */
export function shortStoreLabel(storeId: string): string {
    return `Sin nombre …${storeId.slice(-6)}`;
}

/**
 * Igual, para un vendedor sin nombre. Existe por lo mismo: el ULID crudo ocupa toda la columna y no
 * le dice nada a nadie.
 */
export function shortSellerLabel(sellerId: string): string {
    return `Vendedor …${sellerId.slice(-6)}`;
}

/**
 * Los nombres de tienda que el análisis conoce, para cruzarlos con la rotación.
 *
 * 🔴 Toma el mix Y la cohorte a propósito. El mix solo trae las tiendas que vendieron EN LA VENTANA
 * ACTUAL, así que sacar los nombres solo de ahí hacía que la tabla de rotación —cuyos números no
 * dependen del rango— se renombrara sola al cambiar de pill: la misma tienda era «Celular Express»
 * en 7 días y «Tienda …000006» en Hoy. La cohorte cubre además la ventana anterior, y es justo la
 * tienda que dejó de vender la que este tablero busca señalar.
 */
export function storeNamesFrom(
    mix: StoreFinancierMixRow[],
    cohort: StoreCohortRow[] = [],
): Map<string, string> {
    const names = new Map<string, string>();
    for (const row of [...cohort, ...mix]) {
        if (row.storeName !== null) names.set(row.storeId, row.storeName);
    }
    return names;
}

/** Lo mínimo de un bloque de rango que necesitan los dos mapas de abajo. */
interface RangeBlockLike {
    financierMix: StoreFinancierMixRow[];
    storeCohort: StoreCohortRow[];
}

/**
 * La ZONA de cada tienda, juntando **todos** los rangos de la respuesta.
 *
 * 🔴 Nunca del rango activo. La zona es un atributo del padrón —no cambia porque mires otra ventana
 * de tiempo— pero el único bloque que la trae es `financierMix`, donde solo están las tiendas que
 * vendieron en ese rango. Armándola con el rango activo, Iztapalapa salía «Iztapalapa / —» en Hoy y
 * «Iztapalapa / Zona Centro» en 30 días con exactamente los mismos números al lado: la etiqueta
 * bailaba sola y hacía dudar del resto de la fila.
 *
 * Los nombres ya mergeaban dos fuentes y las zonas no: esa asimetría era el bug.
 */
export function zonesFromRanges(ranges: Record<string, RangeBlockLike>): Map<string, string> {
    const zones = new Map<string, string>();
    for (const block of Object.values(ranges)) {
        for (const row of block.financierMix) {
            if (row.zoneName) zones.set(row.storeId, row.zoneName);
        }
    }
    return zones;
}

/**
 * Los NOMBRES de tienda juntando todos los rangos, por el mismo motivo que las zonas: los números de
 * la tabla de rotación no dependen del rango, así que sus etiquetas tampoco pueden.
 */
export function storeNamesFromRanges(ranges: Record<string, RangeBlockLike>): Map<string, string> {
    const names = new Map<string, string>();
    for (const block of Object.values(ranges)) {
        for (const [storeId, name] of storeNamesFrom(block.financierMix, block.storeCohort)) {
            names.set(storeId, name);
        }
    }
    return names;
}

/**
 * `+12%` / `—`. **La usa `DeltaChip`**, que es quien renderiza: tenerla exportada y sin consumidor
 * hacía que su test diera una protección falsa — el bug de la doble escala podía volver por dentro
 * del componente con la suite en verde.
 *
 * 🔴 El valor YA viene en escala de porcentaje y redondeado desde el silo; aquí NO se vuelve a
 * escalar. Multiplicarlo otra vez pintaba `+10000%` donde iba `+100%`.
 *
 * El `+` sale SOLO cuando de verdad subió. Antes la condición era `>= 0`, así que un cero —el
 * redondeo de cualquier caída menor a medio punto— se pintaba `+0%` y afirmaba una subida que no
 * hubo. (El `-0` de `Math.round(-0.3)` es un caso teórico: no sobrevive al JSON, `JSON.stringify(-0)`
 * devuelve `"0"`. Lo que llega es un cero normal, y por eso el arreglo está en el operador.)
 */
export function formatChange(change: number | null): string {
    if (change === null) return '—';
    // El `+` SOLO cuando de verdad subió. `Math.round(-0.3)` da `-0` en JS y `-0 >= 0` es `true`, así
    // que una CAÍDA se pintaba `+0%`; y `String(-0)` es `'0'`, así que tampoco sale un `-0%` raro.
    // Cero es cero: sin signo, sin afirmar dirección.
    return `${change > 0 ? '+' : ''}${String(change)}%`;
}

/** Un porcentaje del silo, o guion. Nunca un `0%` que en realidad es «no sé». */
export function formatShare(share: number | null): string {
    return share === null ? '—' : `${String(share)}%`;
}

/** El top de modelos sin renglones sin SKU: la venta sin teléfono no tiene modelo que rankear. */
export function rankedSkus<TRow extends { sku: string | null }>(
    rows: readonly TRow[],
): (TRow & { sku: string })[] {
    return rows.filter((row): row is TRow & { sku: string } => row.sku !== null);
}

/** `Samsung Galaxy A25`. Si el silo no conoce el modelo, el SKU es la mejor etiqueta que hay. */
export function skuLabel(row: {
    sku: string;
    productBrand: string | null;
    productModel: string | null;
}): string {
    const name = [row.productBrand, row.productModel].filter(Boolean).join(' ');
    return name || row.sku;
}

/**
 * La ventana que el silo leyó, en formato corto: `9 jul – 7 ago`.
 *
 * 🔴 Se formatea en **CDMX**, no en UTC. El silo corta el día en medianoche de México, así que el
 * `to` de hoy es `T05:59:59.999Z` del día SIGUIENTE — leerlo en UTC pintaba «7 ago – 8 ago» para un
 * rango de un solo día.
 *
 * Se usa para decir la verdad sobre el periodo: la etiqueta del pill dice «Mes en curso» y la
 * ventana real es móvil de 30 días.
 */
export function formatWindow(from: string, to: string): string {
    const fmt = new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        timeZone: 'America/Mexico_City',
    });
    return `${fmt.format(new Date(from))} – ${fmt.format(new Date(to))}`;
}

/**
 * `$12.3K` · `$1.2M` · `$450`.
 *
 * 🔴 Antes dividía entre mil UNA sola vez, así que $12.3M se pintaba `$12345.7K` y se desbordaba de
 * la columna. En dev no se notaba porque el máximo era $195K; una red de celulares a 30 días pasa el
 * millón sin esfuerzo.
 */
export function moneyShort(cents: number): string {
    const pesos = cents / 100;
    const abs = Math.abs(pesos);
    if (abs >= 1_000_000) return `$${(pesos / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(pesos / 1_000).toFixed(1)}K`;
    return `$${String(Math.round(pesos))}`;
}

/** Piezas con separador de miles. `1234` se lee mal pegado a otras columnas numéricas. */
export function formatUnits(units: number): string {
    return units.toLocaleString('es-MX');
}

/**
 * `1 venta` / `5 ventas` — el número Y la palabra que le corresponde.
 *
 * Para frases que YA traen su propio artículo o número, usa `pick`: este helper antepone el conteo y
 * pasarle «el día previo» produjo «Comparativa contra 1 el día previo», que no es español.
 */
export function plural(count: number, singular: string, pluralForm: string): string {
    return `${String(count)} ${count === 1 ? singular : pluralForm}`;
}

/**
 * Elige entre dos formas SIN anteponer el número. Para cuando la frase ya lo trae, o no lo lleva.
 *
 * `pick(1, 'el día previo', 'los N días previos')` → «el día previo».
 * Existe porque `plural` se usó para esto y salió «Comparativa contra 7 los días previos».
 */
export function pick(count: number, singular: string, pluralForm: string): string {
    return count === 1 ? singular : pluralForm;
}
